import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { MinimalSidebar } from './components/ui/minimal-sidebar';
import ModernUpdateFeed from './pages/ModernUpdateFeed';
import NAMAIChat from './pages/Chat';
import ManualIngest from './pages/ManualIngest';
import MyLibrary from './pages/MyLibrary';
import AdminPage from './pages/AdminPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import Map from './pages/MapHUD';
import EntityDirectory from './pages/EntityDirectory';
import EntityProfile from './pages/EntityProfile';
import Reports from './pages/Reports';
import CAHome from './pages/CAHome';
import Compare from './pages/Compare';
import Standards from './pages/Standards';
import Login from './pages/Login';
import PDFUpload from './pages/PDFUpload';
import ChatHistory from './pages/ChatHistory';
import { RbcAttributionFooter } from './components/RbcAttribution';


const AppContent: React.FC = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  // Client-side auth guard: the static build is served directly by nginx for all
  // SPA routes, so the server's requireAuth never runs for in-app navigation.
  // Auth status (and the user's role / home Conservation Authority) come from the
  // shared AuthContext, which verifies the session against the backend.
  // 'member' accounts behave like admins except for the Admin, PDF Upload and
  // Reports routes, which redirect to the feed (the APIs behind them are gated
  // server-side too — these guards are only about not rendering a dead page).
  const { status, isCAUser, isMember, isAdmin } = useAuth();

  if (isLoginPage) {
    return <Login />;
  }

  if (status === 'loading') {
    return <div style={{ height: '100vh', width: '100vw' }} />;
  }

  if (status === 'unauthed') {
    return <Navigate to="/login" replace />;
  }

  return (
    <div style={{ display:'flex', height:'100vh', width:'100vw', overflow:'hidden' }}>
      <MinimalSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/update-feed" element={<ModernUpdateFeed />} />
          <Route path="/nam-ai" element={<NAMAIChat />} />
          <Route path="/manual-ingest" element={<ManualIngest />} />
          <Route path="/library" element={<MyLibrary />} />
          <Route path="/map" element={<Map />} />
          <Route path="/entities" element={<EntityDirectory />} />
          <Route path="/entities/profile" element={<EntityProfile />} />
          <Route path="/analytics-dashboard" element={<AnalyticsDashboard />} />
          <Route path="/reports" element={isMember ? <Navigate to="/update-feed" replace /> : <Reports />} />
          <Route path="/ca-home" element={<CAHome />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/standards" element={<Standards />} />
          <Route path="/admin" element={isMember ? <Navigate to="/update-feed" replace /> : <AdminPage />} />
          <Route path="/pdf-upload" element={isMember ? <Navigate to="/update-feed" replace /> : <PDFUpload />} />
          {/* Chat history is admin-only (it exposes every user's transcripts). */}
          <Route path="/chat-history" element={isAdmin ? <ChatHistory /> : <Navigate to="/update-feed" replace />} />
          <Route path="*" element={<Navigate to={isCAUser ? '/ca-home' : '/update-feed'} replace />} />
        </Routes>
      </div>
      <RbcAttributionFooter />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;
